import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  IconButton,
  InputLabel,
  Link,
  OutlinedInput,
  Stack,
  Typography,
} from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import Modal, { CommonModalProps, ModalContext } from "..";
import { DbOrderManualTaskServiceInfos } from "../../../Core/Api/Types/db-order-manual-task-service-infos";
import { produce } from "immer";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ArrowUpwardOutlinedIcon from "@mui/icons-material/ArrowUpwardOutlined";
import ArrowDownwardOutlinedIcon from "@mui/icons-material/ArrowDownwardOutlined";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { DbOrderManualTaskTasksInfos } from "../../../Core/Api/Types/db-order-manual-task-tasks-infos";
import { OrderManualTaskTasksPriceRule } from "../../../Core/Api/Types/order-manual-task-tasks-price-rule";
import { OrderManualTaskTasksPriceRuleTypes } from "../../../Core/Api/Types/order-manual-task-tasks-price-rule-types";
import { OrderManualTaskTasksPriceRuleMode } from "../../../Core/Api/Types/order-manual-task-tasks-price-rule-mode";
import { TranskodeVideoProfile } from "../../../Core/Api/Types/transkode-video-profile";
import { CurrencyTypes } from "../../../Core/Api/Types/currency-types";
import { planningUserTaskService } from "../../../Core/Api/services";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import FormInputText from "../../Forms/FormInputText/form-input-text";
import { GetSectionIsValid } from "../../../Redux/Reducers/redux-slice-form-validation";
import { useAppSelector } from "../../../Redux/redux-store";
import ModalConfirmAction, { ModalConfirmActionElementProps } from "../ModalConfirmAction/modal-confirm-action";
import FormAutocomplete from "../../Forms/FormAutocomplete/form-autocomplete";
import { useTranslation } from "react-i18next";

export type ModalTaskManagerElementProps = {
  task?: DbOrderManualTaskTasksInfos;
  context: "Create" | "Update";
  services: DbOrderManualTaskServiceInfos[];
  onConfirm?: () => void;
};

export type ModalTaskManagerProps = CommonModalProps<ModalTaskManagerElementProps>;

const ModalTaskManager = React.forwardRef<any, ModalTaskManagerProps>((props, ref) => {
  const { t } = useTranslation();
  const { closeModal, onConfirm, task, context, services } = props;
  const enqueueCustomSnackbar = useCustomSnackbar();
  const isValid = useAppSelector(GetSectionIsValid("form-create-task", "sectionTask"));
  const modalContext = useContext(ModalContext);
  const { openModal } = { ...modalContext };

  const priceRuleTypes = Object.keys(OrderManualTaskTasksPriceRuleTypes);
  const priceRuleModes = Object.keys(OrderManualTaskTasksPriceRuleMode);
  const priceRuleCurrencies = Object.keys(CurrencyTypes);
  const videoProfileTypes = ["SD", "HD", "UHD", "4K"];

  const [newTask, setNewTask] = useState<DbOrderManualTaskTasksInfos>(
    task ? task : new DbOrderManualTaskTasksInfos({ canBeDisabled: false, createdDate: new Date(), isDisabled: false })
  );
  const [newPriceRule, setNewPriceRule] = useState<OrderManualTaskTasksPriceRule>(new OrderManualTaskTasksPriceRule());
  const [priceRules, setPriceRules] = useState<OrderManualTaskTasksPriceRule[]>(
    newTask.priceRules ? newTask.priceRules : []
  );

  const handleCreate = () => {
    planningUserTaskService.CreateManualTasksTask(newTask).then((response) => {
      if (response.success) {
        enqueueCustomSnackbar(t("alert:success.templateCreated"), "success");
        onConfirm && onConfirm();
      } else enqueueCustomSnackbar(response.message!, "error");
    });
    closeModal();
  };

  const handleUpdate = () => {
    planningUserTaskService.UpdateManualTasksTask(newTask).then((response) => {
      if (response.success) {
        enqueueCustomSnackbar(t("alert:success.templateUpdated"), "success");
        onConfirm && onConfirm();
      } else enqueueCustomSnackbar(response.message!, "error");
    });
    closeModal();
  };

  const handleDelete = () => {
    closeModal();

    if (!openModal) return;

    openModal<ModalConfirmActionElementProps>(ModalConfirmAction, {
      displayComment: true,
      commentLabel: t("modal:body.deleteTaskConfirm"),
      title: t("modal:title.deleteTask"),
      onConfirm: () => {
        planningUserTaskService.DeleteManualTasksTask(newTask.id).then((response) => {
          if (response.success) {
            enqueueCustomSnackbar(t("alert:success.templateDeleted"), "success");
          } else {
            enqueueCustomSnackbar(response.message!, "error");
          }
        });
        onConfirm && onConfirm();
      },
    });
  };
  useEffect(() => {
    // Update priceRules
    setNewTask(
      produce((draft) => {
        draft!.priceRules = priceRules;
      })
    );
  }, [priceRules]);

  return (
    <Modal
      title={t("modal:title.taskManager")}
      reference={ref}
      closeModal={() => closeModal()}
      width={900}
      footer={
        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <Button onClick={() => closeModal()}>{t("common:close")}</Button>
          {context === "Create" && (
            <Button color="success" variant="outlined" onClick={handleCreate} disabled={!isValid}>
              {t("common:create")}
            </Button>
          )}
          {context === "Update" && (
            <>
              <Button color="error" variant="outlined" onClick={handleDelete}>
                {t("common:delete")}
              </Button>
              <Button color="success" variant="outlined" onClick={handleUpdate} disabled={!isValid}>
                {t("common:update")}
              </Button>
            </>
          )}
        </Stack>
      }
    >
      <Typography variant="body2" color="text.tier" mb={2}>
        {context === "Create"
          ? t("modal:body.createNewTask")
          : t("modal:body.updateExistingTask", { taskId: newTask.id })}
      </Typography>

      <Grid container spacing={2}>
        <Grid item md={4}>
          <FormInputText
            label={t("common:name")}
            value={newTask?.name}
            onChange={(v) => {
              setNewTask(
                produce((draft) => {
                  draft!.name = v.target.value;
                })
              );
            }}
            placeholder={t("common:name")}
            rows={3}
            validationProps={{
              formId: "form-create-task",
              formSectionId: "sectionTask",
              fieldId: "Name",
              initialState: false,
              required: true,
            }}
          />
        </Grid>
        <Grid item md={4}>
          <FormInputText
            label={t("common:description")}
            value={newTask?.description}
            onChange={(v) => {
              setNewTask(
                produce((draft) => {
                  draft!.description = v.target.value;
                })
              );
            }}
            placeholder={t("common:description")}
            rows={3}
            validationProps={{
              formId: "form-create-task",
              formSectionId: "sectionTask",
              fieldId: "Description",
              initialState: false,
              required: true,
            }}
          />
        </Grid>
        <Grid item md={4}>
          <InputLabel sx={{ mb: 1 }}>{t("field:label.erpTaskCode")}</InputLabel>
          <OutlinedInput
            sx={{ width: "100%", mb: 2 }}
            value={newTask?.erpTaskCode}
            placeholder={t("common:erpTaskCode")}
            onChange={(v) => {
              setNewTask(
                produce((draft) => {
                  draft!.erpTaskCode = v.target.value;
                })
              );
            }}
          />
        </Grid>
      </Grid>

      <InputLabel sx={{ mt: 2, overflow: "visible", mb: 1 }}>{t("common:comment")}</InputLabel>
      <OutlinedInput
        sx={{ width: "100%", mb: 2 }}
        value={newTask?.comment}
        placeholder={t("common:lowerCaseComment")}
        onChange={(v) => {
          setNewTask(
            produce((draft) => {
              draft!.comment = v.target.value;
            })
          );
        }}
      />

      {<InputLabel sx={{ mt: 2 }}>{t("common:prices")}</InputLabel>}
      {newTask?.prices?.map((price) => (
        <Grid container spacing={2}>
          <Grid item md={6}>
            <Chip sx={{ mt: 1 }} label={price.currency} color="warning" size="medium" />
          </Grid>
          <Grid item md={6}>
            <OutlinedInput
              sx={{ width: "100%", mb: 2 }}
              type="number"
              inputProps={{ min: 0, step: 0.00000000001 }}
              value={price.price}
              onChange={(e) => {
                setNewTask(
                  produce((draft) => {
                    let item = draft.prices?.find((x) => x.currency === price.currency);
                    if (item) {
                      item.price = parseFloat(e.target.value);
                    }
                  })
                );
              }}
            />
          </Grid>
        </Grid>
      ))}

      <FormAutocomplete
        label={t("common:services")}
        validationProps={{
          formId: "form-create-task",
          formSectionId: "sectionTask",
          fieldId: "Services",
          initialState: false,
          required: true,
        }}
        onChange={(v) => {
          setNewTask(
            produce((draft) => {
              draft!.serviceId = v ? v!.id : null;
            })
          );
        }}
        placeholder={t("field:placeholder.selectProductType")}
        options={services}
        value={services.find((x) => x.id === newTask.serviceId)}
        getOptionLabel={(option: any) => option.name}
        filterOptions={(x, s) => x.filter((a) => a.name?.includesAllWords(s.inputValue))}
      />

      <InputLabel sx={{ mt: 2, mb: 1, overflow: "visible" }}>{t("field:label.priceRulesDefinitions")}</InputLabel>
      <Alert
        icon={<InfoOutlinedIcon />}
        color="info"
        sx={{ mb: 1.5, mt: 1.5, fontWeight: 1, fontSize: 13, width: "auto" }}
      >
        {t("alert:info.youCanDefinePriceRule")}
      </Alert>

      {priceRules.length > 0 && (
        <Box
          sx={{
            border: "1px solid",
            borderColor: "backgroundAlpha.main",
            borderRadius: "5px",
            padding: "15px",
            bgcolor: "backgroundAlpha.main",
          }}
        >
          <Grid container spacing={2}>
            <Grid item md={2}>
              <Typography variant="body2" color="text.tier">
                {t("common:matchType")}
              </Typography>
            </Grid>

            <Grid item md={2}>
              <Typography variant="body2" color="text.tier">
                {t("common:value")}
              </Typography>
            </Grid>

            <Grid item md={2}>
              <Typography variant="body2" color="text.tier">
                {t("common:priceValue")}
              </Typography>
            </Grid>

            <Grid item md={2}>
              <Typography variant="body2" color="text.tier">
                {t("common:currency")}
              </Typography>
            </Grid>

            <Grid item md={2}>
              <Typography variant="body2" color="text.tier">
                {t("common:priceMode")}
              </Typography>
            </Grid>

            <Grid item md={2}>
              <Typography variant="body2" color="text.tier">
                {t("common:actions")}
              </Typography>
            </Grid>
          </Grid>

          <Divider sx={{ mt: 1, mb: 1 }} />

          {priceRules.map((priceRule, index) => (
            <Grid container spacing={2}>
              <Grid item md={2}>
                <Typography>{priceRule.type}</Typography>
              </Grid>

              <Grid item md={2}>
                <Typography sx={{ mb: 1 }}>
                  {priceRule.videoProfile ? priceRule.videoProfile : priceRule.min + " to " + priceRule.max + " min"}
                </Typography>
              </Grid>

              <Grid item md={2}>
                <Typography>{priceRule?.price!.price}</Typography>
              </Grid>

              <Grid item md={2}>
                <Typography>{priceRule?.price!.currency}</Typography>
              </Grid>

              <Grid item md={2}>
                <Typography sx={{ mb: 1 }}>{priceRule.mode}</Typography>
              </Grid>

              <Grid item md={1}>
                <Link
                  href="#"
                  underline="hover"
                  variant="subtitle1"
                  color="error"
                  fontWeight={500}
                  fontSize={14}
                  onClick={() => {
                    setPriceRules(
                      produce((draft) => {
                        let index = draft.findIndex(
                          (x) =>
                            x.mode === priceRule.mode &&
                            x.type === priceRule.type &&
                            x.videoProfile === priceRule.videoProfile
                        );
                        if (index !== -1) draft.splice(index, 1);
                      })
                    );
                  }}
                >
                  <DeleteIcon fontSize="small" />
                  {t("common:remove")}
                </Link>
              </Grid>

              <Grid item md={0.5}>
                {priceRules && priceRules[index + 1] !== undefined && (
                  <IconButton
                    onClick={() => {
                      setPriceRules(
                        produce((draft) => {
                          let index = draft!.findIndex(
                            (x) =>
                              x.mode === priceRule.mode &&
                              x.type === priceRule.type &&
                              x.videoProfile === priceRule.videoProfile
                          );
                          const itemToMove = draft![index!];
                          draft![index!] = draft![index! + 1];
                          draft![index! + 1] = itemToMove;
                        })
                      );
                    }}
                  >
                    <ArrowDownwardOutlinedIcon sx={{ width: "18px", height: "18px" }} />
                  </IconButton>
                )}
              </Grid>
              <Grid item md={0.5}>
                {index !== 0 && (
                  <IconButton
                    onClick={() => {
                      setPriceRules(
                        produce((draft) => {
                          let index = draft!.findIndex(
                            (x) =>
                              x.mode === priceRule.mode &&
                              x.type === priceRule.type &&
                              x.videoProfile === priceRule.videoProfile
                          );
                          const itemToMove = draft![index!];
                          draft![index!] = draft![index! - 1];
                          draft![index! - 1] = itemToMove;
                        })
                      );
                    }}
                  >
                    <ArrowUpwardOutlinedIcon sx={{ width: "18px", height: "18px" }} />
                  </IconButton>
                )}
              </Grid>
            </Grid>
          ))}
        </Box>
      )}

      <Grid container spacing={2} mt={2}>
        <Grid item md={3}>
          <InputLabel sx={{ mb: 1 }}>{t("common:matchType")}</InputLabel>
          <Autocomplete
            // sx={{ flexGrow: 2 }}
            options={priceRuleTypes}
            value={newPriceRule.type}
            onChange={(e, v) => {
              setNewPriceRule(
                produce((draft) => {
                  draft!.type = v as OrderManualTaskTasksPriceRuleTypes;
                })
              );
            }}
            renderInput={(params) => (
              <OutlinedInput
                {...params.InputProps}
                inputProps={params.inputProps}
                required
                sx={{ width: "100%" }}
                placeholder={t("field:placeholder.selectATask")}
              />
            )}
          />
        </Grid>

        {newPriceRule.type === OrderManualTaskTasksPriceRuleTypes.MediaVideoProfile && (
          <>
            <Grid item md={3}>
              <InputLabel sx={{ mb: 1 }}>{t("field:label.videoProfile")}</InputLabel>
              <Autocomplete
                options={videoProfileTypes}
                value={newPriceRule.videoProfile}
                onChange={(e, v) => {
                  setNewPriceRule(
                    produce((draft) => {
                      draft!.videoProfile = v as TranskodeVideoProfile;
                    })
                  );
                }}
                renderInput={(params) => (
                  <OutlinedInput
                    {...params.InputProps}
                    inputProps={params.inputProps}
                    required
                    sx={{ width: "100%" }}
                    placeholder={t("field:placeholder.selectATask")}
                  />
                )}
              />
            </Grid>
          </>
        )}

        {newPriceRule.type === OrderManualTaskTasksPriceRuleTypes.MediaDuration && (
          <>
            <Grid item md={3}>
              <Grid container spacing={2}>
                <Grid item md={6}>
                  <InputLabel sx={{ mb: 1 }}>{t("field:label.minValue")}</InputLabel>
                  <OutlinedInput
                    sx={{ width: "100%", mb: 2 }}
                    type="number"
                    value={newPriceRule.min}
                    onChange={(v) => {
                      setNewPriceRule(
                        produce((draft) => {
                          draft!.min = parseInt(v.target.value);
                        })
                      );
                    }}
                  />
                </Grid>
                <Grid item md={6}>
                  <InputLabel sx={{ mb: 1 }}>{t("field:label.maxValue")}</InputLabel>
                  <OutlinedInput
                    sx={{ width: "100%", mb: 2 }}
                    type="number"
                    value={newPriceRule.max}
                    onChange={(v) => {
                      setNewPriceRule(
                        produce((draft) => {
                          draft!.max = parseInt(v.target.value);
                        })
                      );
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>
          </>
        )}

        {newPriceRule.type === null && (
          <Grid item md={3}>
            <Stack>
              <InputLabel sx={{ mb: 1 }}>{t("field:label.valueInputParameter")}</InputLabel>
              <Typography mt={1} variant="body2" color="text.tier">
                {t("modal:modalTaskManager.waitingForMatchType")}
              </Typography>
            </Stack>
          </Grid>
        )}

        <Grid item md={2}>
          <InputLabel sx={{ mb: 1 }}>{t("field:label.priceMode")}</InputLabel>
          <Autocomplete
            options={priceRuleModes}
            value={newPriceRule.mode}
            onChange={(e, v) => {
              setNewPriceRule(
                produce((draft) => {
                  draft!.mode = v as OrderManualTaskTasksPriceRuleMode;
                })
              );
            }}
            renderInput={(params) => (
              <OutlinedInput
                {...params.InputProps}
                inputProps={params.inputProps}
                required
                sx={{ width: "100%" }}
                placeholder={t("field:placeholder.selectATask")}
              />
            )}
          />
        </Grid>

        <Grid item md={3}>
          <InputLabel sx={{ mb: 1 }}>{t("field:label.priceSelection")}</InputLabel>
          <Grid container spacing={2}>
            <Grid item md={6}>
              <OutlinedInput
                sx={{ width: "100%", mb: 2 }}
                type="number"
                placeholder={t("field:placeholder.enterPrice")}
                onChange={(v) => {
                  setNewPriceRule(
                    produce((draft) => {
                      if (draft!.price) draft!.price.price = parseInt(v.target.value);
                    })
                  );
                }}
              />
            </Grid>

            <Grid item md={6}>
              <Autocomplete
                options={priceRuleCurrencies}
                onChange={(e, v) => {
                  setNewPriceRule(
                    produce((draft) => {
                      draft!.price!.currency = v as CurrencyTypes;
                    })
                  );
                }}
                renderInput={(params) => (
                  <OutlinedInput
                    {...params.InputProps}
                    inputProps={params.inputProps}
                    required
                    sx={{ width: "100%" }}
                    placeholder={t("field:placeholder.selectATask")}
                  />
                )}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid item md={1}>
          <InputLabel sx={{ mb: 1 }}>{t("common:add")}</InputLabel>
          <IconButton
            disabled={
              newPriceRule.mode === undefined ||
              newPriceRule.mode === null ||
              newPriceRule.type === undefined ||
              (newPriceRule.type === "MediaDuration" &&
                (newPriceRule.min === undefined || newPriceRule.max === undefined)) ||
              (newPriceRule.type === "MediaVideoProfile" && newPriceRule.videoProfile === undefined) ||
              newPriceRule.price === undefined ||
              newPriceRule.price?.price === undefined ||
              newPriceRule.price.currency === undefined
            }
            onClick={() => {
              setPriceRules(
                produce((draft) => {
                  draft!.push(newPriceRule);
                })
              );
              setNewPriceRule(new OrderManualTaskTasksPriceRule());
            }}
          >
            <AddIcon sx={{ width: "18px", height: "18px" }} />
          </IconButton>
        </Grid>
      </Grid>
    </Modal>
  );
});

export default ModalTaskManager;
